import { motion } from "framer-motion";
import { Clock, MapPin, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface ScheduleItem {
  time: string;
  location: string;
  map_link?: string;
}

interface ScheduleTimelineProps {
  scheduleItems: ScheduleItem[];
  className?: string;
}

export const ScheduleTimeline = ({ scheduleItems, className = "" }: ScheduleTimelineProps) => {
  if (!scheduleItems || scheduleItems.length === 0) {
    return null;
  }

  const isFirst = (index: number) => index === 0;
  const isLast = (index: number) => index === scheduleItems.length - 1;

  return (
    <Card className={`bg-gradient-to-br from-primary-50 to-white border-primary-200 ${className}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-primary-100 rounded-full">
            <Clock className="w-4 h-4 text-primary-600" />
          </div>
          <h3 className="text-base font-bold text-secondary-800">여행 일정</h3>
        </div>
        
        <div className="relative">
          {/* Timeline line - 전체 배경 라인 */}
          <div className="absolute left-2.5 md:left-3 top-4 bottom-4 w-0.5 bg-secondary-200" />
          
          <div className="space-y-2">
            {scheduleItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="relative flex items-start gap-2"
              >
                {/* Timeline node */}
                <div className="relative z-10 flex-shrink-0">
                  <div
                    className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center ${
                      isFirst(index)
                        ? "bg-success-500 border-success-600"
                        : isLast(index)
                        ? "bg-info-500 border-info-600"
                        : "bg-primary-500 border-primary-600"
                    }`}
                  >
                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                  </div>
                  
                  {/* Active line segment */}
                  {!isLast(index) && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "calc(100% + 0.5rem)" }}
                      transition={{ delay: (index + 1) * 0.05, duration: 0.2 }}
                      className={`absolute top-5 md:top-6 left-1/2 -translate-x-1/2 w-0.5 ${
                        isFirst(index)
                          ? "bg-gradient-to-b from-success-500 to-primary-500"
                          : "bg-primary-500"
                      }`}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 + 0.1, duration: 0.2 }}
                    className={`rounded-md p-2 ${
                      isFirst(index)
                        ? "bg-success-50 border border-success-200"
                        : isLast(index)
                        ? "bg-info-50 border border-info-200"
                        : "bg-white border border-secondary-200"
                    } shadow-sm`}
                  >
                    {/* Time and Status in one line */}
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${
                          isFirst(index)
                            ? "bg-success-100 text-success-700"
                            : isLast(index)
                            ? "bg-info-100 text-info-700"
                            : "bg-primary-100 text-primary-700"
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        {item.time}
                      </div>
                      
                      {/* Status badge */}
                      {isFirst(index) && (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-success-100 text-success-700 rounded">
                          출발
                        </span>
                      )}
                      {isLast(index) && (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-info-100 text-info-700 rounded">
                          도착
                        </span>
                      )}
                    </div>

                    {/* Location and Map link in one line */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        <MapPin className="w-3 h-3 text-secondary-500 flex-shrink-0" />
                        <p className="text-sm font-medium text-secondary-800 truncate">
                          {item.location}
                        </p>
                      </div>
                      
                      {/* Map link */}
                      {item.map_link && (
                        <motion.a
                          href={item.map_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 flex-shrink-0"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <ExternalLink className="w-3 h-3" />
                          지도보기
                        </motion.a>
                      )}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};